/* @HEADER
 * @version 1.1.1397 | 2026-11-01
 * @purpose Owns authenticated project-registry effects, including independently triggered reversible quarantine of board copies proven to belong to another project.
 * @sidecar useProjectRegistrySyncEffects.ts.header.md
 * @layer app | @hex adapter | @ctx workspace
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-8816, ZVX-DEV-8719, ZVX-DEV-8715, ZVX-DEV-8169, ZVX-DEV-8098, ZVX-DEV-7627, ZVX-DEV-7440, ZVX-DEV-7404, ZVX-DEV-7219, ZVX-DEV-7231, ZVX-DEV-7136, ZVX-DEV-6700, ZVX-DEV-6698, ZVX-DEV-6193, ZVX-DEV-6182, ZVX-DEV-5935, ZVX-DEV-2724, ZVX-DEV-5020, ZVX-DEV-3277, ZVX-DEV-2737, ZVX-DEV-5479, ZVX-DEV-3257, ZVX-DEV-2726

import { useEffect, useRef } from 'react'
import type { AuthPort } from '../auth/AuthContext'
import type { JwtAuthPort } from '../auth/JwtAuthAdapter.ts'
import { refreshMembershipsFromServer } from '../auth/refreshMembershipsFromServer'
import { MEMBERSHIPS_REFRESHED_EVENT } from '../auth/membershipsRefreshedEvent.ts'
import { readAccountIdentity } from '../features/account/public-api'
import { startSharedCanvasRepublishWiring } from '../features/canvas/sharedCanvasRepublishWiring'
import { startSharedBoardRepublishWiring } from '../features/kanban/sharedBoardRepublishWiring'
import { startSharedSpaceRepublishWiring } from '../features/content-access/sharedSpaceRepublishWiring'
import { startSharedBoardReceiveWiring } from '../features/kanban/sharedBoardReceiveWiring'
import { startSharedSpaceReceiveWiring } from '../features/content-access/sharedSpaceReceiveWiring'
// @ts-expect-error -- plain .mjs helper, no local .d.ts; see boardCatalogSpacePlacement.mjs's JSDoc for the shape.
import { planBoardSpaceRelocations } from '../features/content-p2p/boardCatalogSpacePlacement.mjs'
// @ts-expect-error -- plain .mjs application service is directly covered by node:test.
import { runForeignBoardCopyRecovery } from '../features/content-p2p/foreignBoardCopyRecovery.mjs'
// @ts-expect-error -- plain .mjs application service is directly covered by node:test.
import { relocateParkedBoardsInOtherProjects } from '../features/content-p2p/relocateParkedBoardsInOtherProjects.mjs'
import { isRelocateParkedBoardsEverywhereEnabled } from '../shared/featureFlags.ts'
// @ts-expect-error -- plain .mjs application service is directly covered by node:test.
import { runForeignBoardRecoveryBestEffort, runRegistryPostSyncPasses } from '../features/content-p2p/registryPostSyncRecovery.mjs'
import { createJwtFetch } from '../lib/api-client/jwt-fetch.ts'
import { setProjectKey, withDbLock, syncActiveProjectSpaces, pushAllProjectsSpaces, SPACE_REGISTRY_DIRTY_EVENT, getDbName, setDbName } from '../lib/db'
import { planAmbientPointerRebind, AMBIENT_POINTER_REBOUND_EVENT } from '../lib/db/ambientPointerRebind.ts'
import { useProjectSwitch } from '../app/ProjectSwitchContext'
import { startMoveOffRemovedProject, type MoveHost } from '../lib/moveOffRemovedProject.ts'
import { startMoveOffLeftOrganisation, type LeftOrganisationMoveHost } from '../lib/moveOffLeftOrganisation.ts'
import { PROJECT_REMOVAL_LEDGER_KEY } from '../lib/projectRemovalLedger.ts'
import { autoRepairPersonalProjectKeys } from '../lib/db/autoProjectKeyRepair.ts'
import { renameProvisionalCardsInAllProjects } from '../lib/db/taskData'
import { getAllBoards, getAllBoardsIn, getAllBoardGroups, getAllBoardGroupsIn, saveBoard, saveBoardIn, mergeBoardIn } from '../lib/db/boardData'
import { getActiveProject, getAllProjects, dedupeProjectsOnBoot, stampProjectOrganizationsWithReport, resolveProjectPrefix, PROJECT_MUTATED_EVENT } from '../lib/projectStore'
import { resolvePersonalWorkspaceId } from '../lib/projectOrgScope'
import { reportUnassignedProjects } from '../lib/projectOrganizationUnassignedReport'
import { useProjectOrganizationsStamped } from './useProjectOrganizationsStamped'
import { triggerProjectRegistrySync, PROJECT_REGISTRY_SYNCED_EVENT, readRegistrySyncedDetail, requestLiveProjectRegistryRefresh, ORG_ACTIVITY_SIGNAL_EVENT, REGISTRY_REFRESH_WANTED_EVENT, LIVE_REGISTRY_REFRESH_HEARTBEAT_MS } from '../lib/projectRegistryBridge.ts'
import { resolveApiBase } from '../shared/config/apiConfig.ts'

/**
 * ZVX-DEV-3257 â€” quiet-period debounce (ms) applied to `PROJECT_MUTATED_EVENT`
 * and `SPACE_REGISTRY_DIRTY_EVENT` before firing the actual registry push.
 * Coalesces a burst of edits (e.g. typing a new project prefix
 * character-by-character in ProjectSwitcher) into ONE push cycle instead of
 * one REST round-trip per keystroke, while still landing well under a minute
 * after the user stops editing.
 */
const REGISTRY_MUTATION_PUSH_DEBOUNCE_MS = 1500

/**
 * ZVX-DEV-5935 -- the effects below are AuthenticatedApp's, moved here as
 * text: same order, same bodies, same empty dependency lists. The only value
 * they read from the component is the session's AuthPort, which is why it is
 * the hook's one argument. Call it once, before the route state, exactly
 * where the effects used to stand.
 */
export function useProjectRegistrySyncEffects(authAdapter: AuthPort): void {
  // ZVX-DEV-8715 -- this component renders again when the organisation stamp lands, and so does every sync mount below it,
  // which reads each project's organisation while it renders. The value is not used; the render is the point.
  useProjectOrganizationsStamped();
  // ZVX-DEV-8169 -- the host that owns the mount, when there is one. The effect below is registered once; it asks the LATEST host
  // at the moment a registry answer arrives.
  const projectSwitch = useProjectSwitch();
  const projectSwitchRef = useRef(projectSwitch);
  projectSwitchRef.current = projectSwitch;
  // ZVX-DEV-8226 -- the host, when it can both move the window and keep what is known about deleted projects.
  const moveHost = (): MoveHost | null => {
    const host = projectSwitchRef.current;
    return host && typeof host.switchProject === 'function' && host.removalLedger && host.deletedNotice ? (host as MoveHost) : null;
  };
  // ZVX-DEV-8269 -- the host, when it can move the window and hold the one banner. The move in flight is kept so that the two
  // signals that can ask for it at once (the registry cycle, the membership refresh) make one switch.
  const leftMoveRef = useRef<Promise<void> | null>(null);
  const startLeftMove = (): Promise<void> | null => {
    if (leftMoveRef.current) return leftMoveRef.current;
    const host = projectSwitchRef.current;
    if (!host || typeof host.switchProject !== 'function' || !host.deletedNotice) return null;
    const moving = startMoveOffLeftOrganisation(host as LeftOrganisationMoveHost);
    if (moving === null) return null;
    const settled = moving.finally(() => {
      leftMoveRef.current = null;
    });
    leftMoveRef.current = settled;
    return settled;
  };
  // ZVX-DEV-2724: reconcile the project registry once per authenticated-online
  // session, covering the RELOAD path (an existing JWT session picked up by
  // the useMemo above) â€” not just a fresh login. LoginPage/VerifyEmailPage/
  // JwtAuthAdapter.login() cover the "just signed in" paths; without this
  // effect a user who reloads with an existing session (the common case)
  // never re-fires the sync, so a project created on another device would
  // never appear until the next explicit login. Runs at most once per mount
  // (empty deps) and only when `authAdapter` is genuinely the JWT-backed
  // adapter (has getAccessToken) AND currently authenticated â€” the legacy
  // offline single-user adapter is never targeted. Uses the standard
  // jwt-fetch wrapper (not a fixed bearer token) so a stale/expired access
  // token still gets silently refreshed before the registry call.
  useEffect(() => {
    const maybeJwtAdapter = authAdapter as unknown as Partial<JwtAuthPort>;
    if (typeof maybeJwtAdapter.getAccessToken !== 'function') return;
    if (!maybeJwtAdapter.isAuthenticated?.()) return;
    // ZVX-DEV-6700 -- do not rely solely on the registry event: a fast boot
    // cycle may predate its listener, while the already-persisted registry is
    // sufficient for the conservative, idempotent local recovery pass.
    void runForeignBoardRecoveryBestEffort(() => runForeignBoardCopyRecovery({
      getProjects: getAllProjects,
      getAllBoardsIn,
      putBoardIn: saveBoardIn,
    }));
    const identity = readAccountIdentity();
    triggerProjectRegistrySync({
      fetchImpl: createJwtFetch(authAdapter as unknown as JwtAuthPort),
      personalWorkspaceId: resolvePersonalWorkspaceId(identity.workspaces),
      removalLedger: projectSwitchRef.current?.removalLedger,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ZVX-DEV-5020 â€” ask the server which organizations this user belongs to,
  // once per authenticated mount, on the same conditions as the registry sync
  // above.
  //
  // The membership list lives in the `localUserIdentity` snapshot, written
  // when a session is issued and never revisited. Measured on the operator's
  // account: he was made Owner of a new organization, reloaded, and it was not
  // there; on a second machine, where he had signed in afresh, it was. Someone
  // who is INVITED hits exactly this and concludes the invitation failed.
  //
  // Deliberately fire-and-forget and deliberately silent on every non-answer:
  // an offline device and an older server both have to leave the stored list
  // alone rather than empty it (see refreshMembershipsFromServer for why an
  // empty list would be the ZVX-DEV-5017 failure by another route).
  useEffect(() => {
    const maybeJwtAdapter = authAdapter as unknown as Partial<JwtAuthPort>;
    if (typeof maybeJwtAdapter.getAccessToken !== 'function') return;
    if (!maybeJwtAdapter.isAuthenticated?.()) return;
    void refreshMembershipsFromServer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ZVX-DEV-3277 â€” LIVE receive path for registry changes. ZVX-DEV-3257 fixed
  // the send side (a rename/key-change/new-Space is pushed within ~1.5s), but
  // every receive-side trigger lived on a boot/login path, so a running
  // session never re-read the registry: the second org member kept seeing the
  // OLD project name, OLD task key, and never saw a newly created Space until
  // they reloaded the tab (the owner-reported bug).
  //
  // Three signals feed the same throttled entry point, chosen so that a quiet
  // session costs nothing:
  //   1. ORG_ACTIVITY_SIGNAL_EVENT â€” emitted from the canvas/board/wiki
  //      freshness-catalog delta handlers. Free (it rides an existing frame)
  //      and fires exactly when the org is demonstrably active.
  //   2. `visibilitychange` â†’ visible â€” covers the common "came back to the
  //      tab" case and costs nothing while the tab is backgrounded.
  //   3. A 2-minute heartbeat, gated on the tab being visible â€” the ONLY
  //      cover for a pure registry mutation on an otherwise silent org (a
  //      rename produces no content delta at all). Deliberately rare.
  //
  // All three funnel through requestLiveProjectRegistryRefresh, which
  // debounces (1.5s), enforces a 30s floor between cycles, skips entirely
  // when offline, and inherits syncProjectRegistry's in-flight guard â€” so N
  // signals can never become N syncs. The APPLY side is not duplicated here:
  // the existing PROJECT_REGISTRY_SYNCED_EVENT handler below reconciles the
  // name/prefix/icon/spaces and re-applies the project key at runtime.
  //
  // Fully inert for the legacy single-user/offline adapter: the effect
  // early-returns before arming any listener or interval.
  useEffect(() => {
    const maybeJwtAdapter = authAdapter as unknown as Partial<JwtAuthPort>;
    if (typeof maybeJwtAdapter.getAccessToken !== 'function') return;
    function requestRefresh(forced = false) {
      if (!maybeJwtAdapter.isAuthenticated?.()) return;
      // Never spend a request on a backgrounded tab â€” whatever changed is
      // picked up by the `visibilitychange` signal the moment it returns.
      //
      // ZVX-DEV-7404 -- except when a card is WAITING for the registry
      // (REGISTRY_REFRESH_WANTED_EVENT): the relay loop that holds the card
      // does not look at visibility, so a hidden tab keeps asking for bodies it
      // cannot place and never for the list that would place them. The floor in
      // `requestLiveProjectRegistryRefresh` still bounds that to one GET per 10 s.
      if (!forced && typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      const identity = readAccountIdentity();
      requestLiveProjectRegistryRefresh({
        fetchImpl: createJwtFetch(authAdapter as unknown as JwtAuthPort),
        personalWorkspaceId: resolvePersonalWorkspaceId(identity.workspaces),
        removalLedger: projectSwitchRef.current?.removalLedger,
      });
    }
    function onVisibilityChange() {
      if (document.visibilityState === 'visible') requestRefresh();
    }
    // The listeners get a closure, not `requestRefresh` itself: an event handler
    // is called with the Event, which must not be read as `forced`.
    const onSignal = () => requestRefresh();
    const onWanted = () => requestRefresh(true);
    window.addEventListener(ORG_ACTIVITY_SIGNAL_EVENT, onSignal);
    window.addEventListener(REGISTRY_REFRESH_WANTED_EVENT, onWanted);
    document.addEventListener('visibilitychange', onVisibilityChange);
    const heartbeat = setInterval(onSignal, LIVE_REGISTRY_REFRESH_HEARTBEAT_MS);
    return () => {
      window.removeEventListener(ORG_ACTIVITY_SIGNAL_EVENT, onSignal);
      window.removeEventListener(REGISTRY_REFRESH_WANTED_EVENT, onWanted);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      clearInterval(heartbeat);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ZVX-DEV-2737 â€” push every local project's pending Space rows once each
  // PROJECT_REGISTRY_SYNCED_EVENT fires (i.e. after syncProjectRegistry's own
  // project push+pull cycle above has run and, crucially, has already
  // resolved/refreshed each project's `serverProjectId`). Closes the
  // confirmed prod gap: syncProjectRegistry pushed PROJECTS but never SPACES,
  // so a discovered project on a second device showed only the seeded
  // default system spaces, never its real ones. ZVX-DEV-2742 (Bug 2) widens
  // this from the previously active-project-only push to ALL of the user's
  // projects with a valid serverProjectId â€” with 10+ local projects, only the
  // one the user happened to have open ever got its real Spaces pushed.
  // Re-runs syncActiveProjectSpaces afterwards so any OTHER space discovered
  // server-side this cycle (e.g. created on a third device) is merged into
  // the ACTIVE project's local mirror too (other projects merge on their own
  // next boot/switch, matching the existing serverSpaces reconcile cadence).
  // Never throws â€” best-effort, retried on the next sync cycle.
  useEffect(() => {
    const maybeJwtAdapter = authAdapter as unknown as Partial<JwtAuthPort>;
    function onRegistrySynced(event: Event) {
      // ZVX-DEV-3257 (Cause 2) â€” the reconcile that just ran may have
      // rewritten the ACTIVE project's `prefix` (e.g. an org-mate renamed
      // the shared project's task-key). Re-resolve it and re-apply
      // immediately so tasks minted AFTER this point use the new prefix,
      // without waiting for a project switch or a full page reload.
      // Existing task_ids are never rewritten â€” id is an identifier, not a
      // label (owner decision). Unconditional: cheap local read, no network.
      setProjectKey(resolveProjectPrefix(getActiveProject()));
      // ZVX-DEV-6737 -- and the pointer beside it. `main.tsx` binds the
      // database name and the project key on two adjacent lines at module
      // load; only the key was ever re-applied here. The reconcile that just
      // ran can be what stamped this project's `dbName` at all -- an org
      // member's project arrives through registry-reconcile, not through
      // `createProject()` -- so the boot answer can name a database that is
      // not this project's. Measured on staging: 3 boards of 12 until the
      // project switcher, the app's only other re-binder, was touched.
      //
      // Inside `withDbLock`, the FIFO queue every ambient mutator takes, for
      // the reason `switchAmbientProject` gives: a name that moves under a
      // section already holding the lock is the defect this pointer has had
      // three times. A failure leaves the pointer where it was and the next
      // cycle asks again.
      //
      // The passes below are CHAINED onto it rather than started beside it,
      // and that is the half that makes the rebind worth anything:
      // `syncActiveProjectSpaces` is what writes this project's Spaces and
      // then announces 'board-data-changed', which is what the Space list
      // re-reads on. Started in parallel, it would race the rebind and win or
      // lose by how long the network took -- ordering by latency, which is
      // the same mistake as ordering by a timer.
      //
      // ZVX-DEV-7440 -- and the app is TOLD when the pointer really moved. A
      // mounted app resolved its database name at mount and never asks again, so
      // the host remakes it on this announcement (see AuthenticatedAppHost for
      // why only on the session's first cycle, which is what `firstCycle` carries
      // here). Announced only when the name actually changed: the plan above was
      // made before the lock, and the lock is a queue -- a project switch ahead
      // of us may already have bound this very name, and that switch has remade
      // the app once already.
      const { firstCycle } = readRegistrySyncedDetail(event);
      // ZVX-DEV-8226 -- first: if the project this window stands on was deleted on another device, the HOST moves the window
      // off it (one switch, every cycle), BEFORE the generic pointer pass below, which then reads the pointer the switch
      // left. `null` is the usual case and costs nothing: the pass below runs in this same tick, as it always did.
      const host = projectSwitchRef.current;
      const mover = moveHost();
      const movedOffDeleted = mover ? startMoveOffRemovedProject(mover) : null;
      // ZVX-DEV-8269 -- otherwise, if the window stands on a project of an organisation the person was removed from (the list the
      // refresh keeps is read now), the same host moves it off. One move per cycle: when the deleted-project move runs, the
      // other reason is asked again by the next cycle or the next membership refresh.
      const movedOff = movedOffDeleted ?? startLeftMove();
      const settlePointer = (): Promise<void> => {
        const rebindTo = planAmbientPointerRebind(getDbName(), getActiveProject());
        // ZVX-DEV-8169 (ZVX-4464 item 23) -- on the session's first cycle, with a host above us, the HOST moves the pointer: it takes
        // the app away, binds the name inside the ambient lock with nothing mounted, and mounts the app again. Binding it here and
        // announcing it left the mounted tree beside a pointer that had already moved until the host heard. The later cycle (the
        // active project vanished from the registry) and a hook with no host above it keep the old path: bind here, announce.
        return rebindTo === null
          ? Promise.resolve()
          : firstCycle && typeof host?.rebindAmbientPointer === 'function'
            ? host.rebindAmbientPointer(rebindTo)
            : withDbLock(async () => {
              const before = getDbName();
              setDbName(rebindTo);
              if (before !== rebindTo) {
                window.dispatchEvent(
                  new CustomEvent(AMBIENT_POINTER_REBOUND_EVENT, { detail: { dbName: rebindTo, firstCycle } }),
                );
              }
            });
      };
      const pointerSettled = movedOff === null
        ? settlePointer()
        : movedOff.catch((error) => console.warn('[project-removed-elsewhere] move failed', error)).then(settlePointer);
      if (typeof maybeJwtAdapter.getAccessToken !== 'function') return;
      if (!maybeJwtAdapter.isAuthenticated?.()) return;
      // ZVX-DEV-7219 -- once the registry has supplied the stored prefix,
      // repair only legacy WS cards in the owner's personal projects. The
      // repair itself checkpoints and revalidates before its atomic write.
      void pointerSettled.then(() => autoRepairPersonalProjectKeys(
        resolvePersonalWorkspaceId(readAccountIdentity().workspaces),
      )).catch((error) => console.warn('[project-key-repair] post-sync pass failed', error));
      // ZVX-DEV-7627 -- a card numbered while the lease was out of reach (KEY-2*)
      // is given a real number once the registry has answered, which only
      // happens signed in and online. The multiplayer subscription that used to
      // do this exists only under a flag no build sets. Best-effort: a failure
      // leaves the star and the next registry cycle asks again.
      void pointerSettled
        .then(() => renameProvisionalCardsInAllProjects(resolveApiBase()))
        .catch((error) => console.warn('[provisional-rename] pass failed', error));
      // ZVX-DEV-6700 -- local recovery is independent from the fallible
      // network Space push. Previously one rejected promise skipped recovery
      // and the final catch concealed that fact in production.
      void pointerSettled.then(() => runRegistryPostSyncPasses({
        reconcileSpaces: async () => {
          await pushAllProjectsSpaces({
            fetchImpl: createJwtFetch(authAdapter as unknown as JwtAuthPort),
            apiBase: resolveApiBase(),
          });
          // ZVX-3805 -- the first cycle of the session announces even when no Space differs; later cycles do not.
          await syncActiveProjectSpaces({ announceAnyway: firstCycle });
        },
        recoverForeignBoards: () => runForeignBoardCopyRecovery({
          getProjects: getAllProjects,
          getAllBoardsIn,
          putBoardIn: saveBoardIn,
        }),
        // ZVX-DEV-3272 -- catch-up pass for a board that arrived BEFORE its
        // Space did. Such a board sits in the default group with the author's
        // `serverSpaceId` remembered on `p2p_desired_space_id` (see
        // planBoardCatalogSpacePlacement); now that the Space reconcile above
        // has run, every one of them can finally move into its real Space.
        // Chained AFTER syncActiveProjectSpaces so it reads the freshly
        // merged boardGroups, never the pre-sync set. Best-effort: a failure
        // just leaves the board where it is until the next sync cycle.
        // ZVX-DEV-3915 -- the read (getAllBoards/getAllBoardGroups) and every
        // relocation write in the loop below are now ONE `withDbLock` unit.
        // Before this, the read and each write were separate ambient
        // `DB_NAME` accesses spread across several awaits with no lock of
        // their own -- exactly the window the org-catalog register loop's
        // `withProjectStorage` critical section (App.tsx, fired every
        // ORG_CATALOG_SNAPSHOT_POLL_MS) can open for a FOREIGN project. Any
        // relocation write landing inside that window would target the
        // foreign project's database instead of this one's, the same
        // mechanism ZVX-DEV-3789/3802/3915 already closed elsewhere. Uses
        // the raw unlocked `getAllBoards`/`getAllBoardGroups`/`saveBoard`
        // (already imported above) INSIDE the lock -- never the (now locked)
        // persistence port -- exactly the shape every other `withDbLock`
        // critical section in this file uses.
        relocateBoards: async () => {
          await withDbLock(async () => {
            const [boards, groups] = await Promise.all([getAllBoards(), getAllBoardGroups()]);
            for (const patch of planBoardSpaceRelocations(boards, groups)) {
              await saveBoard(patch);
            }
          });
          // ZVX-DEV-8816 (ZVX-2566) -- the same catch-up for every OTHER project on the device. The pass above reads the open project only, so a
          // board that arrived before its Space in a project that is not open stayed in the inbox, with its cards, until the person opened it.
          // Addressed reads and writes (no ambient pointer, no lock); one project failing leaves the others done; best-effort like the pass above.
          if (!isRelocateParkedBoardsEverywhereEnabled()) return;
          await relocateParkedBoardsInOtherProjects({
            getProjects: getAllProjects,
            activeDbName: getDbName(),
            getAllBoardsIn,
            getAllBoardGroupsIn,
            mergeBoardIn,
            onProjectError: (dbName: string, error: unknown) => console.warn('[relocate-parked-boards] project skipped', dbName, error),
          }).catch((error: unknown) => console.warn('[relocate-parked-boards] pass failed', error));
        },
      }));
    }
    window.addEventListener(PROJECT_REGISTRY_SYNCED_EVENT, onRegistrySynced);
    return () => window.removeEventListener(PROJECT_REGISTRY_SYNCED_EVENT, onRegistrySynced);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ZVX-DEV-8226 -- another window of this device saw the server delete a project and wrote it down. This window's own registry
  // cycle may find nothing to announce (the list was already replaced from under it), so the ledger itself is the signal.
  useEffect(() => {
    function onLedgerWritten(event: StorageEvent) {
      if (event.key !== PROJECT_REMOVAL_LEDGER_KEY) return;
      const mover = moveHost();
      if (!mover) return;
      void startMoveOffRemovedProject(mover)?.catch((error) => console.warn('[project-removed-elsewhere] move failed', error));
    }
    window.addEventListener('storage', onLedgerWritten);
    return () => window.removeEventListener('storage', onLedgerWritten);
  }, []);

  // ZVX-DEV-5479 â€” a canvas someone else can open is published again when the
  // person editing it changes it.
  //
  // Until this, `announceCanvasForSharing` was the only publisher, and it runs
  // when the sharing panel opens. Everything after that gesture stayed on the
  // owner's device: the operator renamed a shared canvas twice and added a
  // sticker, and his other account kept showing the name from two renames ago
  // on an empty board.
  //
  // Two lines here on purpose. The bounds live in republishSharedCanvas.ts as
  // properties of a pure function â€” only canvases this device has SHARED, only
  // when the content HASH changed, never for a save this device merely RECEIVED
  // â€” and the wiring that reads storage lives beside the announce it reuses.
  useEffect(() => startSharedCanvasRepublishWiring(), []);

  // ZVX-DEV-6182 â€” and a BOARD someone else can open is published again the
  // same way. ZVX-DEV-6166 gave the board its first publish (the panel's
  // door); without this line the recipient kept that first body for ever,
  // because the only other board publisher is the paid background push a
  // personal workspace does not have. Same bounds, same pure plan.
  useEffect(() => startSharedBoardRepublishWiring(), []);

  // ZVX-DEV-7136 â€” and a SPACE someone was granted is published by its owner's
  // app without the panel being opened: once at start for a grant this device
  // never published (a grant from before the Space door had no other way to
  // get a body), then after local edits. The shared set is the SERVER's answer,
  // not a local memory â€” see republishSharedSpace.ts for the four bounds.
  useEffect(() => startSharedSpaceRepublishWiring(), []);

  // ZVX-DEV-6193 â€” and a guest's edit of a shared board comes home. A board
  // has no co-edit session and the catalog consume is off in a personal
  // workspace, so the owner asks: the shared boards' catalog rows, a foreign
  // hash, one pull, writes marked fromRemote so the line above stays silent.
  useEffect(() => startSharedBoardReceiveWiring(), []);

  // ZVX-DEV-7231 â€” receive edits made through a shared Space's own item key.
  useEffect(() => startSharedSpaceReceiveWiring(), []);

  // ZVX-DEV-3257 (Cause 1) â€” push the project registry immediately after a
  // local project mutation (rename, prefix change, org move, icon/hide via
  // ProjectSwitcher) instead of waiting for the next reload/login.
  // projectStore.ts fires PROJECT_MUTATED_EVENT from updateProject()/
  // renameProject(); debounced here so a burst of edits coalesces into a
  // single push cycle. Reuses the same never-throw, offline-gated
  // triggerProjectRegistrySync() the boot/login call sites already use.
  useEffect(() => {
    const maybeJwtAdapter = authAdapter as unknown as Partial<JwtAuthPort>;
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    function onProjectMutated() {
      if (typeof maybeJwtAdapter.getAccessToken !== 'function') return;
      if (!maybeJwtAdapter.isAuthenticated?.()) return;
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        const identity = readAccountIdentity();
        triggerProjectRegistrySync({
          fetchImpl: createJwtFetch(authAdapter as unknown as JwtAuthPort),
          personalWorkspaceId: resolvePersonalWorkspaceId(identity.workspaces),
          removalLedger: projectSwitchRef.current?.removalLedger,
        });
      }, REGISTRY_MUTATION_PUSH_DEBOUNCE_MS);
    }
    window.addEventListener(PROJECT_MUTATED_EVENT, onProjectMutated);
    return () => {
      window.removeEventListener(PROJECT_MUTATED_EVENT, onProjectMutated);
      if (debounceTimer) clearTimeout(debounceTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ZVX-DEV-3257 (Cause 3) â€” push a newly created/renamed Space immediately
  // instead of waiting for the next PROJECT_REGISTRY_SYNCED_EVENT cycle.
  // boardData.ts#saveBoardGroup fires SPACE_REGISTRY_DIRTY_EVENT on every
  // local Space CRUD write; debounced here for the same coalescing reason as
  // the project-mutation effect above. Shares pushAllProjectsSpaces +
  // syncActiveProjectSpaces with the existing PROJECT_REGISTRY_SYNCED_EVENT
  // handler â€” never throws, best-effort, retried on the next cycle.
  useEffect(() => {
    const maybeJwtAdapter = authAdapter as unknown as Partial<JwtAuthPort>;
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    function onSpaceMutated() {
      if (typeof maybeJwtAdapter.getAccessToken !== 'function') return;
      if (!maybeJwtAdapter.isAuthenticated?.()) return;
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        pushAllProjectsSpaces({
          fetchImpl: createJwtFetch(authAdapter as unknown as JwtAuthPort),
          apiBase: resolveApiBase(),
        })
          .then(() => syncActiveProjectSpaces())
          .catch(() => {});
      }, REGISTRY_MUTATION_PUSH_DEBOUNCE_MS);
    }
    window.addEventListener(SPACE_REGISTRY_DIRTY_EVENT, onSpaceMutated);
    return () => {
      window.removeEventListener(SPACE_REGISTRY_DIRTY_EVENT, onSpaceMutated);
      if (debounceTimer) clearTimeout(debounceTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ZVX-DEV-2726 â€” LOCAL project-registry dedupe, unconditional and
  // independent of auth state. Unlike the effect above, this must run even
  // for an expired session (no JWT adapter, or `isAuthenticated()` false) â€”
  // that's exactly the case where the server pull in `syncProjectRegistry`
  // never runs, so its post-pull dedupe never fires either, and existing
  // Project-Switcher duplicates would otherwise persist across every reload.
  // Reads only localStorage (readAccountIdentity + dedupeProjectsOnBoot),
  // no network call.
  useEffect(() => {
    const identity = readAccountIdentity();
    // ZVX-DEV-5314 -- `workspaces[0]` is POSITION, not identity: the server
    // returns memberships in no guaranteed order, so an organization landing
    // first would become this user's "personal" workspace and every unassigned
    // project would be filed under it. ZVX-DEV-4757 fixed three call sites of
    // exactly this and its own note says so; this one survived.
    const personalWorkspaceId = resolvePersonalWorkspaceId(identity.workspaces);
    dedupeProjectsOnBoot(personalWorkspaceId);
    // ZVX-DEV-5314 -- and then write each project's organization down, so
    // nothing downstream has to infer it from an absent field again.
    reportUnassignedProjects(stampProjectOrganizationsWithReport(identity.workspaces).unassigned);
    // ZVX-DEV-8098 -- the stamp above runs once, with the identity the device held at the mount. An identity that
    // lands later in the session (the memberships refresh) left every unstamped project without an organisation
    // until the next registry pull, and for a project without an organisation nothing syncs (ZVX-DEV-7370). The
    // refresh announces itself; stamp again, reading the identity at that moment and not the one taken here.
    const restamp = () => reportUnassignedProjects(stampProjectOrganizationsWithReport(readAccountIdentity().workspaces).unassigned);
    // ZVX-DEV-8269 -- and the list that just changed may no longer name an organisation the window stands in.
    const onRefreshed = () => {
      restamp();
      void startLeftMove()?.catch((error) => console.warn('[left-organisation] move failed', error));
    };
    window.addEventListener(MEMBERSHIPS_REFRESHED_EVENT, onRefreshed);
    return () => window.removeEventListener(MEMBERSHIPS_REFRESHED_EVENT, onRefreshed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
